import {
	ReactElement,
	useMemo,
	useState
} from "react";
import {
	ListItem,
	Icon,
	Space
} from "client/Components";
import {
	FileThumbnailKind,
	getSpriteClassForExtension,
	ONE_NOTE_DOCUMENT_VIRTUAL_EXTENSION
} from "client_legacy/Documents/fileExtensions";
import { DocumentItemType } from "client/Api/Documents/SharePoint/common";
import { ActionAndOthers } from "client_legacy/ndaw/actions";
import { never } from "client_legacy/ndaw/errors";

type DocumentListItemProps<TContext> = {
	name: string;
	subtitle?: string | ReactElement;
	documentKind: DocumentItemType;
	extension: string | nil;
	isShared?: boolean;
	onClick: () => void;
	actions: readonly ActionAndOthers<TContext>[];
	actionContext: TContext;
	trackingId: string;
	isTouch: boolean;
	shouldShowQuickActions: boolean;
};

export const DocumentListItem = <TContext,>(props: DocumentListItemProps<TContext>): ReactElement => {
	const {
		name,
		documentKind,
		extension,
		subtitle,
		isShared = false,
		actions,
		actionContext,
		trackingId,
		isTouch,
		shouldShowQuickActions,
		onClick
	} = props;

	const [isMoreActionsOpen, setIsMoreActionOpen] = useState<boolean>(false);

	const thumbnail = useMemo(
		(): FileThumbnailKind => {
			switch (documentKind)
			{
				case DocumentItemType.OneNoteNotebook:
					return getSpriteClassForExtension(ONE_NOTE_DOCUMENT_VIRTUAL_EXTENSION);
				case DocumentItemType.Folder:
					return "folder";
				case DocumentItemType.File:
					return getSpriteClassForExtension(extension ?? "");
				default:
					never(documentKind);
			}
		},
		[documentKind, extension]
	);

	return (
		<ListItem
			noMobileBorderBottom
			trackingId={trackingId}
		>
			<ListItem.Content
				onClick={onClick}
				thumbnail={
					<Icon
						value={{
							kind: "svg",
							name: thumbnail
						}}
						size="extra-extra-large"
					/>
				}
			>
				<ListItem.Title>
					{name}
					{isShared && (
						<>
							<Space />
							<Icon value="user-group" size="small" />
						</>
					)}
				</ListItem.Title>
				{subtitle && (
					<ListItem.SubTitle>
						{subtitle}
					</ListItem.SubTitle>
				)}
			</ListItem.Content>
			<ListItem.QuickActions
				actions={actions}
				actionContext={actionContext}
				isMoreActionsOpen={isMoreActionsOpen}
				isTouch={isTouch}
				onIsMoreActionOpenChange={setIsMoreActionOpen}
				menuTitle={name}
				maxActionCount={shouldShowQuickActions ? undefined : 0}
			/>
		</ListItem>
	);
};
DocumentListItem.displayName = "DocumentListItem";